import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  ScrollText,
  Server,
  GitBranch,
  Globe,
  Bot,
  Bug,
  ClipboardList,
  Users,
  Search,
  Rocket,
  GitPullRequestArrow,
  ShieldAlert,
  Cpu,
  KeyRound,
  LogOut,
  Menu,
  X,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/contexts/AuthContext'

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/ecs', label: 'ECS', icon: Server },
  { to: '/logs', label: 'Logs', icon: ScrollText },
  { to: '/cicd', label: 'CI/CD', icon: GitBranch },
  { to: '/vercel', label: 'Vercel', icon: Globe },
  { to: '/sentry', label: 'Sentry', icon: Bug },
  { to: '/tasks', label: 'Tasks', icon: ClipboardList },
  { to: '/agent', label: 'Agent', icon: Bot },
]

const adminNavItems = [
  { to: '/users', label: 'Users', icon: Users },
  { to: '/api-keys', label: 'API Keys', icon: KeyRound },
  { to: '/opensearch', label: 'OpenSearch', icon: Search },
  { to: '/release', label: 'Release', icon: Rocket },
  { to: '/review', label: 'Review', icon: GitPullRequestArrow },
  { to: '/bugfix', label: 'Bug Fix', icon: ShieldAlert },
  { to: '/runners', label: 'Runners', icon: Cpu },
]

export function Layout() {
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  return (
    <div className="flex h-screen">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav
        className={cn(
          'w-56 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-bg-card)] flex flex-col z-40',
          'fixed inset-y-0 left-0 transition-transform duration-200 md:relative md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[var(--color-primary)]">
              TWP AIOps
            </h1>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              twp-aiops.tocca.systems
            </p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 py-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-r-2 border-[var(--color-primary)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]'
                )
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          {user?.is_admin && (
            <>
              <div className="mx-4 my-2 border-t border-[var(--color-border)]" />
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                      isActive
                        ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-r-2 border-[var(--color-primary)]'
                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]'
                    )
                  }
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-[var(--color-border)] p-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm text-[var(--color-text)] truncate">{user?.name}</p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              title="Logout"
              className="p-1.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)] transition-colors shrink-0"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header with hamburger */}
        <div className="flex items-center gap-3 p-3 border-b border-[var(--color-border)] md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            <Menu size={20} />
          </button>
          <h1 className="text-sm font-bold text-[var(--color-primary)]">TWP AIOps</h1>
        </div>

        <main className="flex-1 min-h-0 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
